/**
 * KanameRaanaFanPage
 * Aesthetic: Underground Zine × Japanese Music Editorial
 * Motif: Dual Gaze — steel-blue (left) × amber-yellow (right)
 */
import React, { useEffect, useRef } from 'react';

// ─── Design constants ─────────────────────────────────────────────────────────

const GRAIN_SVG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.68' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='0.09'/%3E%3C/svg%3E\")";

const clr = {
  blue: '#4A7FB7',
  yellow: '#FFC843',
  matcha: '#77DD77',
  primary: '#5B49D6',
};

// ─── Data ────────────────────────────────────────────────────────────────────

const profileStats = [
  { label: '생일', value: '2.22', sub: '고양이날 · Pisces' },
  { label: '키', value: '150㎝', sub: 'Hanasakigawa' },
  { label: '밴드', value: 'MyGO!!!!!', sub: 'Roselia 자매 밴드' },
  { label: '포지션', value: 'Lead Guitar', sub: 'ESP POTBELLY' },
  { label: 'CV', value: '아오키 히나', sub: 'Hina Aoki' },
];

const galleryItems = [
  {
    id: 1, label: 'DUAL GAZE', tag: 'concept art',
    src: 'https://placehold.co/800x1000/0A0B0E/4A7FB7?text=',
    span: 'row-span-2',
  },
  {
    id: 2, label: 'STRAY CAT', tag: 'character',
    src: 'https://placehold.co/800x500/0F1014/FFC843?text=',
    span: '',
  },
  {
    id: 3, label: 'SCARLET', tag: 'guitar',
    src: 'https://placehold.co/800x500/111318/77DD77?text=',
    span: '',
  },
  {
    id: 4, label: 'MYGO!!!!!', tag: 'band shot',
    src: 'https://placehold.co/800x500/0F1014/5B49D6?text=',
    span: 'col-span-2',
  },
];

const traits = [
  {
    num: '01',
    title: '핵심 기질',
    body: '직감과 즉흥에 충실한 자유로운 기타 히어로. 논리보다 감각이 먼저 움직인다.',
    accent: clr.blue,
  },
  {
    num: '02',
    title: '밴드 내 역할',
    body: '무대 위 감정선을 단단히 붙잡는 리딩 기타리스트. 자유롭게 합류했지만 음악의 중심이 됐다.',
    accent: clr.yellow,
  },
  {
    num: '03',
    title: '이색동공의 상징',
    body: '파란 왼눈 = 차가운 직관, 앰버 오른눈 = 따뜻한 감성. 길고양이처럼 어디에도 얽매이지 않는 존재.',
    accent: clr.matcha,
  },
];

const bioFacts = [
  { dt: '풀네임', dd: '要 楽奈', sub: 'Ranna Kaname', jp: true },
  { dt: '학교', dd: '하나사키가와 여학원 중학교', sub: '3학년 A반' },
  { dt: '좋아하는 것', dd: '말차 · 소바 · 고양이 · 파르페', sub: '' },
  { dt: '싫어하는 것', dd: '부추 · 마 · 뜨거운 음식', sub: '' },
  { dt: '특기', dd: 'ESP POTBELLY 기타 연주', sub: 'Miraculous Scarlet' },
  { dt: '소속 부활동', dd: '수예부', sub: '' },
];

// ─── Marquee data ────────────────────────────────────────────────────────────

const MARQUEE_ITEMS = [
  'RANNA KANAME', '要 楽奈', 'LEAD GUITAR', 'MYGO!!!!!',
  'DUAL GAZE', '二つの瞳', 'STRAY CAT', 'ESP POTBELLY',
  'HANASAKIGAWA', '길고양이', 'BANG DREAM', 'IT\'S MYGO!!!!!',
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function GrainOverlay({ opacity = 0.5 }: { opacity?: number }) {
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: GRAIN_SVG, backgroundRepeat: 'repeat', backgroundSize: '300px',
        mixBlendMode: 'overlay', opacity, zIndex: 1,
      }}
    />
  );
}

function StringLine({ delay = 0 }: { delay?: number }) {
  return (
    <div style={{ height: 1, position: 'relative', overflow: 'hidden' }}>
      <div
        style={{
          height: '100%',
          background: `linear-gradient(90deg,
            transparent 0%, ${clr.blue}99 12%,
            rgba(238,240,247,0.3) 50%,
            ${clr.yellow}99 88%, transparent 100%)`,
          backgroundSize: '200% 100%',
          animation: `string-shimmer 4s ${delay}s linear infinite`,
        }}
      />
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const KanameRaanaFanPage: React.FC = () => {
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onMove = (e: MouseEvent) => {
      const { left, top, width, height } = hero.getBoundingClientRect();
      hero.style.setProperty('--mx', (((e.clientX - left) / width) - 0.5).toFixed(3));
      hero.style.setProperty('--my', (((e.clientY - top) / height) - 0.5).toFixed(3));
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <div style={{ background: 'var(--bg)', color: 'var(--text)', fontFamily: 'var(--font-body)' }}>

      {/* 要楽奈 kanji background — scoped to fan page only */}
      <div
        aria-hidden
        style={{
          position: 'fixed',
          right: '-2rem',
          bottom: '10vh',
          fontFamily: 'var(--font-serif-jp)',
          fontSize: 'clamp(12rem, 28vw, 22rem)',
          fontWeight: 900,
          color: 'rgba(0, 0, 0, 0.028)',
          pointerEvents: 'none',
          userSelect: 'none',
          zIndex: 0,
          lineHeight: 1,
          writingMode: 'vertical-rl',
        }}
      >
        要楽奈
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          HERO — Full viewport, dual-gaze split
      ══════════════════════════════════════════════════════════════════ */}
      <section
        ref={heroRef}
        aria-labelledby="rana-hero-title"
        style={{
          position: 'relative',
          minHeight: '100svh',
          overflow: 'hidden',
          background: '#070809',
          '--mx': '0',
          '--my': '0',
        } as React.CSSProperties}
      >
        {/* Ambient glow — parallax on mouse */}
        <div
          aria-hidden
          style={{
            position: 'absolute', inset: '-20%', zIndex: 0,
            background: `
              radial-gradient(ellipse 55% 60% at
                calc(18% + calc(var(--mx, 0) * 10%))
                calc(40% + calc(var(--my, 0) * 8%)),
                rgba(74,127,183,0.28) 0%, transparent 65%),
              radial-gradient(ellipse 50% 55% at
                calc(82% + calc(var(--mx, 0) * -8%))
                calc(35% + calc(var(--my, 0) * -6%)),
                rgba(255,200,67,0.22) 0%, transparent 65%)`,
            filter: 'blur(4px)',
            animation: 'glow-drift 20s ease-in-out infinite alternate',
          }}
        />

        {/* Dot-grid background texture */}
        <div
          aria-hidden
          style={{
            position: 'absolute', inset: 0, zIndex: 0,
            backgroundImage: `radial-gradient(circle, rgba(238,240,247,0.08) 1px, transparent 1px)`,
            backgroundSize: '32px 32px',
          }}
        />

        {/* Film grain */}
        <GrainOverlay opacity={0.45} />

        {/* Vertical Japanese watermark */}
        <div
          aria-hidden
          style={{
            position: 'absolute', right: '4vw', top: 0, bottom: 0,
            display: 'flex', alignItems: 'center', zIndex: 1,
            writingMode: 'vertical-rl', textOrientation: 'mixed',
            fontFamily: 'var(--font-serif-jp)', fontWeight: 900,
            fontSize: 'clamp(5rem, 11vw, 10rem)',
            color: 'var(--text)', opacity: 0.055,
            letterSpacing: '0.06em', userSelect: 'none',
          }}
        >
          要楽奈
        </div>

        {/* Left eye indicator */}
        <div
          aria-hidden
          style={{
            position: 'absolute', left: 0, top: '15%', bottom: '15%', width: 2, zIndex: 2,
            background: `linear-gradient(to bottom, transparent, ${clr.blue}80, transparent)`,
          }}
        />

        {/* Right eye indicator */}
        <div
          aria-hidden
          style={{
            position: 'absolute', right: 0, top: '20%', bottom: '20%', width: 2, zIndex: 2,
            background: `linear-gradient(to bottom, transparent, ${clr.yellow}80, transparent)`,
          }}
        />

        {/* ── Content ── */}
        <div
          style={{
            position: 'relative', zIndex: 3,
            display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
            minHeight: '100svh',
            padding: 'clamp(5rem,8vh,7rem) clamp(1.5rem,5vw,5rem) clamp(2.5rem,5vh,4rem)',
          }}
        >
          {/* Top meta row */}
          <div
            className="animate-fade-up stagger-1"
            style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}
          >
            {['BanG Dream! It\'s MyGO!!!!!', 'Lead Guitar', '2023'].map((item, i) => (
              <React.Fragment key={item}>
                {i > 0 && (
                  <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--stroke)', display: 'inline-block' }} />
                )}
                <span
                  style={{
                    fontFamily: 'var(--font-body)', fontSize: '0.7rem',
                    letterSpacing: '0.28em', textTransform: 'uppercase',
                    color: i === 1 ? clr.blue : 'var(--muted)', opacity: i === 1 ? 1 : 0.6,
                  }}
                >
                  {item}
                </span>
              </React.Fragment>
            ))}
          </div>

          {/* ── MAIN TITLE ── */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '4vh 0' }}>
            {/* RANNA — full bleed gradient title */}
            <h1
              id="rana-hero-title"
              className="animate-fade-up stagger-2"
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'min(23vw, 20rem)',
                lineHeight: 0.85,
                letterSpacing: '0.04em',
                margin: 0,
                background: `linear-gradient(
                  95deg,
                  #7BAFD4 0%,
                  #B8CDD8 22%,
                  #EEEEF5 48%,
                  #F5E0A0 70%,
                  #FFC843 100%
                )`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              RANNA
            </h1>

            {/* KANAME — secondary */}
            <div
              className="animate-fade-up stagger-3"
              style={{ display: 'flex', alignItems: 'baseline', gap: '2rem', marginTop: '0.15em', flexWrap: 'wrap' }}
            >
              <h2
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'min(8.5vw, 6.5rem)',
                  lineHeight: 1,
                  letterSpacing: '0.22em',
                  color: 'var(--muted)',
                  opacity: 0.55,
                  margin: 0,
                }}
              >
                KANAME
              </h2>
              <span
                style={{
                  fontFamily: 'var(--font-serif-jp)',
                  fontSize: 'clamp(1.1rem, 2.2vw, 1.6rem)',
                  color: 'var(--muted)',
                  opacity: 0.4,
                  letterSpacing: '0.12em',
                  paddingBottom: '0.2em',
                }}
              >
                要 楽奈
              </span>
            </div>

            {/* Tagline */}
            <p
              className="animate-fade-up stagger-4"
              style={{
                fontFamily: 'var(--font-serif-jp)',
                fontSize: 'clamp(0.85rem, 1.5vw, 1.05rem)',
                color: 'var(--muted)',
                letterSpacing: '0.14em',
                marginTop: 'clamp(1rem, 2.5vh, 2rem)',
                opacity: 0.6,
              }}
            >
              무대 위의 길고양이 — 二つの瞳、一つの旋律。
            </p>
          </div>

          {/* Bottom row — dual-eye CTA + descriptor */}
          <div
            className="animate-fade-up stagger-5"
            style={{
              display: 'flex', flexWrap: 'wrap',
              alignItems: 'flex-end', justifyContent: 'space-between', gap: '2rem',
            }}
          >
            <div style={{ display: 'flex', gap: '0.875rem', flexWrap: 'wrap' }}>
              {/* Primary CTA */}
              <a
                href="#profile"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.8rem 1.75rem', borderRadius: 999,
                  fontFamily: 'var(--font-body)', fontSize: '0.82rem',
                  fontWeight: 600, letterSpacing: '0.08em', textDecoration: 'none',
                  background: 'var(--text)', color: 'var(--bg)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.boxShadow = `0 12px 32px rgba(238,240,247,0.12)`; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = ''; (e.currentTarget as HTMLElement).style.boxShadow = ''; }}
              >
                프로필 보기
              </a>
              {/* Ghost CTA */}
              <a
                href="#gallery"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.8rem 1.75rem', borderRadius: 999,
                  fontFamily: 'var(--font-body)', fontSize: '0.82rem',
                  fontWeight: 500, letterSpacing: '0.08em', textDecoration: 'none',
                  background: 'rgba(238,240,247,0.06)',
                  border: '1px solid rgba(238,240,247,0.14)',
                  color: 'var(--muted)',
                  backdropFilter: 'blur(8px)',
                  transition: 'transform 0.2s, border-color 0.2s',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(238,240,247,0.3)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = ''; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(238,240,247,0.14)'; }}
              >
                갤러리
              </a>
            </div>

            {/* Right side meta */}
            <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              <p style={{ fontSize: '0.7rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: 'var(--muted)', opacity: 0.45 }}>
                Fan archive · 팬 아카이브
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: clr.blue, boxShadow: `0 0 10px ${clr.blue}` }} />
                <span style={{ width: 1, height: 16, background: 'var(--stroke)' }} />
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: clr.yellow, boxShadow: `0 0 10px ${clr.yellow}` }} />
              </div>
            </div>
          </div>
        </div>

        {/* Guitar string at very bottom of hero */}
        <StringLine delay={0} />
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          MARQUEE STRIP
      ══════════════════════════════════════════════════════════════════ */}
      <div
        aria-hidden
        style={{
          overflow: 'hidden', borderBottom: '1px solid var(--stroke)',
          borderTop: '1px solid var(--stroke)',
          background: 'var(--card)',
          padding: '0.75rem 0',
        }}
      >
        <div
          style={{
            display: 'flex', gap: '3rem',
            animation: 'marquee-scroll 30s linear infinite',
            width: 'max-content',
          }}
        >
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
            <span
              key={i}
              style={{
                fontFamily: i % 2 === 0 ? 'var(--font-display)' : 'var(--font-serif-jp)',
                fontSize: i % 2 === 0 ? '0.9rem' : '0.75rem',
                letterSpacing: i % 2 === 0 ? '0.2em' : '0.12em',
                color: i % 3 === 0 ? clr.blue : i % 3 === 1 ? 'var(--muted)' : clr.yellow,
                opacity: 0.6,
                whiteSpace: 'nowrap',
                textTransform: 'uppercase',
              }}
            >
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          STATS STRIP
      ══════════════════════════════════════════════════════════════════ */}
      <div
        id="profile"
        role="list"
        aria-label="캐릭터 기본 정보"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          borderBottom: '1px solid var(--stroke)',
        }}
      >
        {profileStats.map((stat, i) => (
          <div
            key={stat.label}
            role="listitem"
            style={{
              display: 'flex', flexDirection: 'column', gap: 4,
              padding: '1.5rem 1.75rem',
              borderRight: i < profileStats.length - 1 ? '1px solid var(--stroke)' : 'none',
              borderBottom: '1px solid var(--stroke)',
              background: 'var(--card)',
              transition: 'background 0.2s',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--card-hover)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'var(--card)'; }}
          >
            <span style={{ fontSize: '0.65rem', letterSpacing: '0.24em', textTransform: 'uppercase', color: 'var(--muted)', opacity: 0.55 }}>
              {stat.label}
            </span>
            <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text)', letterSpacing: '0.02em' }}>
              {stat.value}
            </span>
            <span style={{ fontSize: '0.68rem', color: 'var(--muted)', opacity: 0.45 }}>
              {stat.sub}
            </span>
          </div>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          PROFILE — Biography + Facts
      ══════════════════════════════════════════════════════════════════ */}
      <section aria-labelledby="profile-title">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0,3fr) minmax(0,2fr)',
            borderBottom: '1px solid var(--stroke)',
          }}
        >
          {/* Biography column */}
          <div
            style={{
              padding: 'clamp(2.5rem,6vw,5rem) clamp(1.5rem,5vw,4rem)',
              borderRight: '1px solid var(--stroke)',
              display: 'flex', flexDirection: 'column', gap: '2rem',
            }}
          >
            {/* Section label */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span
                style={{
                  fontFamily: 'var(--font-display)', fontSize: '3.5rem', lineHeight: 1,
                  color: clr.blue, opacity: 0.2,
                }}
              >
                02
              </span>
              <div>
                <p style={{ fontSize: '0.65rem', letterSpacing: '0.28em', textTransform: 'uppercase', color: clr.blue, marginBottom: 4 }}>
                  Character
                </p>
                <h2
                  id="profile-title"
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 'clamp(1.8rem, 3.5vw, 3rem)',
                    letterSpacing: '0.05em', lineHeight: 1, margin: 0,
                    color: 'var(--text)',
                  }}
                >
                  BIOGRAPHY
                </h2>
              </div>
            </div>

            {/* Body text */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              {[
                '카나메 라나는 하나사키가와 여자중학교 3학년이자 MyGO!!!!!의 리드 기타리스트입니다. 왼쪽 눈은 강철빛 파란색, 오른쪽 눈은 황금빛 앰버 색으로 빛나는 이색동공을 가졌으며, 자유분방한 성격 덕분에 "길고양이"라는 별명을 얻었습니다.',
                '할머니 츠즈키 시후네의 ESP POTBELLY "Miraculous Scarlet"을 물려받아 매일 라이브하우스 SPACE에서 연주했습니다. 공간이 폐점하면서 숨 쉴 곳을 잃었지만, 토모리와 MyGO!!!!!를 만나 "다시 호흡할 수 있게 됐다"고 말합니다.',
                '좋아하는 것과 싫어하는 것을 거침없이 말하는 솔직함이 특징. 말차와 소바, 파르페를 좋아하고 고양이와 노는 것을 최애 취미로 꼽습니다.',
              ].map((para, i) => (
                <p
                  key={i}
                  style={{
                    fontSize: 'clamp(0.875rem, 1.3vw, 0.95rem)',
                    lineHeight: 1.85, color: 'var(--muted)', margin: 0,
                  }}
                >
                  {para}
                </p>
              ))}
            </div>

            {/* Dual-eye signature line */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.5rem' }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: clr.blue, boxShadow: `0 0 14px ${clr.blue}80`, flexShrink: 0 }} />
              <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${clr.blue}, ${clr.yellow})` }} />
              <span style={{ fontFamily: 'var(--font-serif-jp)', fontSize: '0.75rem', color: 'var(--muted)', opacity: 0.5, whiteSpace: 'nowrap' }}>
                二つの瞳
              </span>
              <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${clr.yellow}, ${clr.blue})` }} />
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: clr.yellow, boxShadow: `0 0 14px ${clr.yellow}80`, flexShrink: 0 }} />
            </div>
          </div>

          {/* Facts column */}
          <div
            style={{
              padding: 'clamp(2.5rem,6vw,5rem) clamp(1.5rem,4vw,3rem)',
              background: 'var(--card)',
              display: 'flex', flexDirection: 'column', gap: '0',
            }}
          >
            <p style={{ fontSize: '0.65rem', letterSpacing: '0.28em', textTransform: 'uppercase', color: clr.yellow, marginBottom: '1.75rem' }}>
              基本情報
            </p>
            <dl style={{ display: 'flex', flexDirection: 'column' }}>
              {bioFacts.map(({ dt, dd, sub, jp }, i) => (
                <div
                  key={dt}
                  style={{
                    display: 'flex', flexDirection: 'column', gap: 3,
                    padding: '1rem 0',
                    borderBottom: i < bioFacts.length - 1 ? '1px solid var(--stroke)' : 'none',
                  }}
                >
                  <dt style={{ fontSize: '0.65rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted)', opacity: 0.5 }}>
                    {dt}
                  </dt>
                  <dd
                    style={{
                      margin: 0,
                      fontFamily: jp ? 'var(--font-serif-jp)' : 'inherit',
                      fontSize: jp ? '1.1rem' : '0.88rem',
                      fontWeight: jp ? 700 : 500,
                      color: 'var(--text)',
                    }}
                  >
                    {dd}
                  </dd>
                  {sub && (
                    <span style={{ fontSize: '0.7rem', color: 'var(--muted)', opacity: 0.45 }}>
                      {sub}
                    </span>
                  )}
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          TRAITS — 3-column
      ══════════════════════════════════════════════════════════════════ */}
      <div
        role="list"
        aria-label="캐릭터 특징"
        style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
          borderBottom: '1px solid var(--stroke)',
        }}
      >
        {traits.map((trait, i) => (
          <article
            key={trait.num}
            role="listitem"
            style={{
              display: 'flex', flexDirection: 'column', gap: '1.25rem',
              padding: 'clamp(2rem,4vw,3rem) clamp(1.5rem,3vw,2.5rem)',
              borderRight: i < traits.length - 1 ? '1px solid var(--stroke)' : 'none',
              position: 'relative', overflow: 'hidden',
              transition: 'background 0.3s',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--card)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ''; }}
          >
            {/* Accent top border */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: trait.accent, opacity: 0.6 }} />

            <span
              style={{
                fontFamily: 'var(--font-display)', fontSize: '4rem', lineHeight: 1,
                color: trait.accent, opacity: 0.18, userSelect: 'none',
              }}
            >
              {trait.num}
            </span>
            <h3 style={{ fontSize: '0.7rem', letterSpacing: '0.22em', textTransform: 'uppercase', color: trait.accent, margin: 0 }}>
              {trait.title}
            </h3>
            <p style={{ fontSize: '0.875rem', lineHeight: 1.75, color: 'var(--muted)', margin: 0 }}>
              {trait.body}
            </p>
            <div style={{ marginTop: 'auto', height: 1, width: '2.5rem', background: trait.accent, opacity: 0.35 }} />
          </article>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          GALLERY — Asymmetric grid
      ══════════════════════════════════════════════════════════════════ */}
      <section id="gallery" aria-labelledby="gallery-title">
        {/* Section header */}
        <div
          style={{
            display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
            padding: '2rem clamp(1.5rem,4vw,3rem)',
            borderBottom: '1px solid var(--stroke)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '1.25rem' }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: '3rem', color: clr.matcha, opacity: 0.18, lineHeight: 1 }}>03</span>
            <div>
              <p style={{ fontSize: '0.65rem', letterSpacing: '0.28em', textTransform: 'uppercase', color: clr.matcha, margin: '0 0 2px' }}>Fan Art</p>
              <h2
                id="gallery-title"
                style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.5rem,3vw,2.5rem)', letterSpacing: '0.06em', margin: 0, color: 'var(--text)' }}
              >
                GALLERY
              </h2>
            </div>
          </div>
          <a
            href="/gallery"
            style={{ fontSize: '0.7rem', letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--muted)', textDecoration: 'none', opacity: 0.5, transition: 'opacity 0.2s' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '0.5'; }}
          >
            전체 보기 →
          </a>
        </div>

        {/* Asymmetric grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr',
            gridTemplateRows: 'auto auto',
            gap: 1,
            background: 'var(--stroke)',
          }}
        >
          {galleryItems.map((item, i) => (
            <figure
              key={item.id}
              style={{
                margin: 0, position: 'relative', overflow: 'hidden',
                gridRow: i === 0 ? 'span 2' : undefined,
                aspectRatio: i === 0 ? '4/5' : i === 3 ? '16/6' : '4/3',
                gridColumn: i === 3 ? 'span 2' : undefined,
                cursor: 'pointer',
                background: 'var(--card)',
              }}
            >
              <img
                src={item.src}
                alt={`${item.label} — ${item.tag}`}
                loading="lazy"
                style={{
                  width: '100%', height: '100%', objectFit: 'cover',
                  display: 'block',
                  transition: 'transform 0.5s cubic-bezier(0.22,1,0.36,1)',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLImageElement).style.transform = 'scale(1.04)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLImageElement).style.transform = ''; }}
              />
              {/* Overlay */}
              <figcaption
                style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  padding: '3rem 1.25rem 1.25rem',
                  background: 'linear-gradient(to top, rgba(7,8,9,0.85) 0%, transparent 100%)',
                  display: 'flex', flexDirection: 'column', gap: 3,
                }}
              >
                <span style={{ fontSize: '0.6rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: 'var(--muted)', opacity: 0.6 }}>
                  {item.tag}
                </span>
                <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', letterSpacing: '0.12em', color: 'var(--text)' }}>
                  {item.label}
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          QUOTE SPOTLIGHT
      ══════════════════════════════════════════════════════════════════ */}
      <section
        aria-label="명대사"
        style={{
          position: 'relative', overflow: 'hidden',
          padding: 'clamp(4rem,10vw,8rem) clamp(1.5rem,8vw,7rem)',
          background: 'var(--card)',
          borderTop: '1px solid var(--stroke)',
          borderBottom: '1px solid var(--stroke)',
        }}
      >
        <GrainOverlay opacity={0.3} />

        {/* Big decorative quote mark */}
        <div
          aria-hidden
          style={{
            position: 'absolute', top: '-0.15em', left: 'clamp(1rem,4vw,3rem)',
            fontFamily: 'var(--font-serif-jp)', fontSize: 'clamp(10rem,22vw,18rem)',
            lineHeight: 1, color: 'var(--text)', opacity: 0.04,
            userSelect: 'none', zIndex: 0,
          }}
        >
          "
        </div>

        {/* Background RANNA */}
        <div
          aria-hidden
          style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'var(--font-display)', fontSize: 'min(26vw, 20rem)',
            color: 'var(--text)', opacity: 0.025, userSelect: 'none', zIndex: 0,
            letterSpacing: '0.05em',
          }}
        >
          RANNA
        </div>

        <div
          style={{
            position: 'relative', zIndex: 1,
            maxWidth: '58rem', margin: '0 auto', textAlign: 'center',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem',
          }}
        >
          <p style={{ fontSize: '0.65rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: 'var(--muted)', opacity: 0.5 }}>
            Quote · 名台詞
          </p>

          <blockquote style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <p
              style={{
                fontFamily: 'var(--font-serif-jp)',
                fontSize: 'clamp(0.9rem, 1.6vw, 1.1rem)',
                letterSpacing: '0.14em', color: 'var(--muted)', opacity: 0.55,
              }}
            >
              「今、この音を、出したい。」
            </p>
            <p
              style={{
                fontSize: 'clamp(1.4rem, 3vw, 2rem)',
                fontWeight: 600, lineHeight: 1.5,
                letterSpacing: '-0.01em', color: 'var(--text)',
              }}
            >
              "나는… 지금, 이 소리를 내고 싶어."
            </p>
          </blockquote>

          <cite
            style={{
              fontStyle: 'normal', fontSize: '0.8rem',
              letterSpacing: '0.14em', color: 'var(--muted)', opacity: 0.5,
            }}
          >
            — Ranna Kaname · BanG Dream! It's MyGO!!!!!
          </cite>

          {/* Dual-eye pills */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.5rem' }}>
            <div style={{ width: 32, height: 1, background: clr.blue, opacity: 0.5 }} />
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: clr.blue, boxShadow: `0 0 12px ${clr.blue}` }} />
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: clr.yellow, boxShadow: `0 0 12px ${clr.yellow}` }} />
            <div style={{ width: 32, height: 1, background: clr.yellow, opacity: 0.5 }} />
          </div>
        </div>
      </section>

    </div>
  );
};

export default KanameRaanaFanPage;
